import { ReservationModel } from '@/api/createCart'
import { BoughtServicesRecord, QuoteReservation } from '@/models/quote'
import { TimeSchedule } from '@/models/service'
import { getTimeScheduleKey } from '@/utils/timeSchedules'

const COMMISSION_SERVICE_IDS = [512, 1613]

function serviceDayToIndex(day: number, startDate: number): number {
  if (day >= 1_000_000_000) {
    return Math.round((day - startDate) / 86_400) + 1
  }

  return day
}

function mapServicesToBoughtServices(
  services: ReservationModel['services'],
  startDate: number
): BoughtServicesRecord {
  return services.reduce<BoughtServicesRecord>((acc, service) => {
    if (
      service.bought <= 0 ||
      COMMISSION_SERVICE_IDS.includes(service.service_id)
    ) {
      return acc
    }

    const startTime = service.start_time ?? '00:00'
    const endTime = service.end_time ?? '23:59'
    const scheduleKey = getTimeScheduleKey({ endTime, startTime })
    const day = service.day ? serviceDayToIndex(service.day, startDate) : 1

    if (!acc[service.service_id]) {
      acc[service.service_id] = {}
    }

    const existing = acc[service.service_id][scheduleKey]
    if (!existing) {
      acc[service.service_id][scheduleKey] = {
        bought: service.bought,
        days: [day],
        serviceId: service.service_id,
        serviceName: service.service_name,
        timeSchedule: { endTime, startTime } as TimeSchedule,
      }
      return acc
    }

    if (!existing.days.includes(day)) {
      existing.days.push(day)
      existing.days.sort((a, b) => a - b)
    }

    existing.bought = Math.max(existing.bought, service.bought)

    return acc
  }, {})
}

export function mapReservationModelsToQuoteReservations(
  models: ReservationModel[]
): QuoteReservation[] {
  return models
    .filter((model) => !model.deleted)
    .map((model) => ({
      beds: model.beds,
      boughtServices: mapServicesToBoughtServices(
        model.services,
        model.start_date
      ),
      chairs: model.chairs,
      deckChairs: model.deck_chairs,
      endDate: model.end_date,
      halfDay: model.half_day,
      maxiBeds: model.maxi_beds,
      notes: model.notes ?? '',
      seasonal: model.seasonal,
      sectorId: model.sector?.id ?? 0,
      sectorName: model.sector?.name ?? '',
      shouldShowSpotName: model.shouldShowSpotName,
      spotName: model.spot_name,
      spotType: model.spot_type as QuoteReservation['spotType'],
      startDate: model.start_date,
      vehiclePlate:
        typeof model.vehicle_plate === 'string'
          ? model.vehicle_plate
          : undefined,
    }))
}

export function isSpotReservation(reservation: QuoteReservation): boolean {
  return Boolean(reservation.spotName && reservation.spotType)
}

export function getPeriodFromReservationModels(models: ReservationModel[]) {
  const activeModels = models.filter((model) => !model.deleted)
  if (activeModels.length === 0) {
    return null
  }

  const startDate = Math.min(...activeModels.map((model) => model.start_date))
  const endDate = Math.max(...activeModels.map((model) => model.end_date))

  return {
    endDate,
    halfDay: activeModels[0].half_day,
    seasonal: activeModels.some((model) => model.seasonal),
    startDate,
  }
}

export function findMapElementIdForReservation(
  elements: { id: string | number; name: string; subType: string }[],
  reservation: QuoteReservation
): number | null {
  if (!reservation.spotName || !reservation.spotType) {
    return null
  }

  const element = elements.find(
    (item) =>
      item.name === reservation.spotName &&
      item.subType === reservation.spotType
  )

  if (!element) {
    return null
  }

  return Number(element.id)
}
